// src/pages/ProductsPage.jsx
import React, { useState, useEffect, useCallback, useMemo, useContext, useRef } from 'react';
import { motion } from "framer-motion";
import { showToast } from '../utils/helpers';
import ProductsList from '../components/products/ProductsList';
import CreateProduct from '../components/products/CreateProduct';
import CustomersList from '../components/customers/CustomersList';
import CreateCustomer from '../components/customers/CreateCustomer';
import CreateCategory from '../components/products/CreateCategory';
import ComingSoon from '../components/common/ComingSoon';
import api from '../utils/api';
import { API_CONFIG } from '../utils/apiConfig';
import { useNavigate, useLocation } from 'react-router-dom';
import OrdersPage from './OrdersPage';
import GenerateAccountCard from '../components/dashboard/GenerateAccountCard';
import AccountUnderReview from '../components/dashboard/AccountUnderReviewCard';
import { useAuth } from '../contexts/AuthContext';
import GetNewAccountCard from '../components/dashboard/GetNewAccountCard';
import { ThemeContext, useTheme } from '../components/context/themeContext';


// Constants for better maintainability
const TAB_CONFIG = {
  CUSTOMERS: 'customers',
  CREATE_CUSTOMER: 'create-customer',
  PRODUCTS: 'products',
  CREATE_PRODUCT: 'create',
  MeoAI: 'meo-ai',
  CATEGORY: 'category',
  ORDERS: 'orders'
};

const ProductsPage = () => {
  const initialTab = () => {
    const hash = window.location.hash.replace('#', '');
    const validTabs = ['customers', 'create-customer', 'products', 'create', 'meo-ai', 'category', 'orders'];
    if (hash && validTabs.includes(hash)) return hash;
    return TAB_CONFIG.PRODUCTS;
  };
  const [activeTab, setActiveTab] = useState(initialTab);
    const { currentUser, currentAccountDetails, existingAccount, refreshUserData, logout } = useAuth();
  
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [pagination, setPagination] = useState({});
  const [currentLimit, setCurrentLimit] = useState(10);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
const { theme, toggleTheme } = useContext(ThemeContext);
  const [companyData, setCompanyData] = useState({
    company_name: '',
    company_id: '',
    username: ''
  });
  const navigate = useNavigate();
  const location = useLocation();
  const [isCopying, setIsCopying] = useState(false);

  // Sync active tab with URL hash (works with React Router navigation)
  useEffect(() => {
    const hash = location.hash.replace('#', '');
    const validTabs = [
      TAB_CONFIG.CUSTOMERS,
      TAB_CONFIG.CREATE_CUSTOMER,
      TAB_CONFIG.PRODUCTS,
      TAB_CONFIG.CREATE_PRODUCT,
      TAB_CONFIG.MeoAI,
      TAB_CONFIG.CATEGORY,
      TAB_CONFIG.ORDERS
    ];

    if (hash && validTabs.includes(hash)) {
      setActiveTab(hash);
      localStorage.setItem('productsPageActiveTab', hash);
    } else if (!hash) {
      // No hash → default to Products
      setActiveTab(TAB_CONFIG.PRODUCTS);
      localStorage.setItem('productsPageActiveTab', TAB_CONFIG.PRODUCTS);
    }
  }, [location.hash]);

  const baseUrl = window.location.origin;
  // const shareLink = companyData.username
  //   ? `${baseUrl}/s/${companyData.username}`
  //   : `${baseUrl}/s/${companyData.company_id}`;
  const shareLink =  companyData.username ? `${companyData.username}.web.paymeo.co` : `${baseUrl}/s/${companyData.company_id}`;


  // Copy to clipboard handler
  const copyToClipboard = useCallback(async () => {
    if (!navigator.clipboard) {
      showToast('Clipboard not supported', 'error');
      return;
    }

    try {
      setIsCopying(true);
      await navigator.clipboard.writeText(shareLink);
      showToast('Link copied to clipboard!', 'success');
      
      // Auto-reset copying state after 2 seconds
      const timeoutId = setTimeout(() => setIsCopying(false), 2000);
      return () => clearTimeout(timeoutId);
    } catch (err) {
      console.error('Failed to copy:', err);
      showToast('Failed to copy link', 'error');
      setIsCopying(false);
    }
  }, [shareLink]);

  const tabsRef = useRef(null);

  // Scroll active tab into view
  useEffect(() => {
    if (!tabsRef.current) return;
    const activeBtn = tabsRef.current.querySelector(`[data-tab-id="${activeTab}"]`);
    if (activeBtn) {
      activeBtn.scrollIntoView({ behavior: "smooth", inline: "center", block: "nearest" });
    }
  }, [activeTab]);

  // Tab change handler with URL hash update
  const handleTabChange = useCallback((tabId) => {
    setActiveTab(tabId);
    localStorage.setItem('productsPageActiveTab', tabId);
    navigate(`#${tabId}`, { replace: true });
  }, [navigate]);
  // Handle account generation
const handleGenerateAccount = async (formData) => {
  try {

    const submitData = new FormData();
    submitData.append("countryCode", formData.countryCode);
    submitData.append('phoneNumber', formData.phoneNumber);
    submitData.append('companyName', formData.companyName);
    submitData.append('userId', formData.userId);
    submitData.append('ownsBusiness', formData.ownsBusiness.toString());
    
    if (formData.ownsBusiness && formData.rc_number) {
      submitData.append('rc_number', formData.rc_number);
    }
    
    if (formData.ownsBusiness && formData.business_certificate) {
      submitData.append('business_certificate', formData.business_certificate);
    }

    // Payment account / virtual account creation is temporarily disabled.
    // const response = await api.post('/app/api/generate-account', submitData, {
    //   headers: {
    //     'Content-Type': 'multipart/form-data',
    //   },
    // });
    const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

    if (response.data.success) {
      // Handle different success scenarios based on response
      if (response.data.requires_verification) {
        // Business verification submitted case
        showToast(
          'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
          'success',
          8000 // Longer duration for important message
        );
        
        // Don't refresh user data or reload page for verification submissions
        // The account is not created yet, so we keep the modal open for user to see the message
        return { requiresVerification: true };
      } else {
        // Normal account creation case
        showToast('Account generated successfully!', 'success');
        await refreshUserData();
        setTimeout(() => {
          window.location.reload();
        }, 1000);
        return { requiresVerification: false };
      }
    } else {
      throw new Error(response.data.message || 'Failed to generate account');
    }
  } catch (error) {
    console.error('Account generation error:', error);
    
    // Enhanced error handling with specific messages
    let errorMessage = 'Failed to generate account';
    
    if (error.response?.data?.message) {
      errorMessage = error.response.data.message;
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    showToast(errorMessage, 'error');
    throw error;
  }
}

  // Creating NEw accounts 
    const handleNewWalletAccount = async (formData) => {
    try {
  
      const submitData = new FormData();
      submitData.append("countryCode", formData.countryCode);
      submitData.append('phoneNumber', formData.phoneNumber);
      submitData.append('companyName', formData.companyName);
      submitData.append('userId', formData.userId);
      submitData.append('ownsBusiness', formData.ownsBusiness.toString());
      
      if (formData.ownsBusiness && formData.rc_number) {
        submitData.append('rc_number', formData.rc_number);
      }
      
      if (formData.ownsBusiness && formData.business_certificate) {
        submitData.append('business_certificate', formData.business_certificate);
      }

      // Payment account / virtual account creation is temporarily disabled.
      // const response = await api.post('/app/api/generate-existing-account', submitData, {
      //   headers: {
      //     'Content-Type': 'multipart/form-data',
      //   },
      // });
      const response = { data: { success: false, message: 'Account generation is temporarily disabled' } };

      if (response.data.success) {
        // Handle different success scenarios based on response
        if (response.data.requires_verification) {
          // Business verification submitted case
          showToast(
            'Your business documents have been submitted successfully and are under review. Your account will be verified within 48 hours. You can update other information, but your account will remain unverified until manual verification is completed.',
            'success',
            8000 // Longer duration for important message
          );
          
          // Don't refresh user data or reload page for verification submissions
          // The account is not created yet, so we keep the modal open for user to see the message
          return { requiresVerification: true };
        } else {
          // Normal account creation case
          showToast('Account generated successfully!', 'success');
          await refreshUserData();
          setTimeout(() => {
            window.location.reload();
          }, 1000);
          return { requiresVerification: false };
        }
      } else {
        throw new Error(response.data.message || 'Failed to generate account');
      }
    } catch (error) {
      console.error('Account generation error:', error);
      
      // Enhanced error handling with specific messages
      let errorMessage = 'Failed to generate account';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      throw error;
    }
  };
  // Data loading functions
  const loadProducts = useCallback(async (page = currentPage, limit = currentLimit, append = false) => {
    try {
      if (append) {
        setIsLoadingMore(true);
      } else {
        setIsLoading(true);
      }
      
      const response = await api.post(`${API_CONFIG.PRODUCTS.MY_PRODUCTS}?page=${page}&limit=${limit}`);
      
      if (append) {
        // Append new products to existing ones
        setProducts(prev => [...prev, ...(response.data.products || [])]);
      } else {
        // Replace products for new page
        setProducts(response.data.products || []);
      }
      
      setPagination(response.data.pagination || {});
    } catch (error) {
      console.error('Error loading products:', error);
      showToast('Failed to load products', 'error');
    } finally {
      setIsLoading(false);
      setIsLoadingMore(false);
    }
  }, [currentPage, currentLimit]);

  // Handle loading more products for infinite scroll
  const handleLoadMore = useCallback(async (page) => {
    await loadProducts(page, currentLimit, true);
  }, [loadProducts, currentLimit]);

  // Handle page change
  const handlePageChange = useCallback((newPage) => {
    setCurrentPage(newPage);
  }, []);

  // Handle limit change
  const handleLimitChange = useCallback((newLimit) => {
    setCurrentLimit(newLimit);
    setCurrentPage(1); // Reset to first page when changing limit
  }, []);

  // Reload products when page or limit changes
  useEffect(() => {
    if (activeTab === TAB_CONFIG.PRODUCTS) {
      loadProducts(currentPage, currentLimit, false);
    }
  }, [currentPage, currentLimit, activeTab, loadProducts]);

  const productSubmitted = () => {
    try {
      handleTabChange("products");
      // Reload products to show the newly created one
      loadProducts(1, currentLimit, false);
    } catch (error) {
      console.log(error);
    }
  };

  const loadCompanyData = useCallback(async () => {
    try {
      const userData = localStorage.getItem('userData');
      if (userData) {
        const parsedData = JSON.parse(userData);
        setCompanyData({
          company_name: parsedData.company_name || 'Your Company',
          company_id: parsedData.company_id || '',
          username: parsedData.username || ''
        });
      }
    } catch (error) {
      console.error('Error loading company data:', error);
      showToast('Error loading company information', 'error');
    }
  }, []);

  // Initial data load
  useEffect(() => {
    const initializeData = async () => {
      await loadCompanyData();
      if (activeTab === TAB_CONFIG.PRODUCTS) {
        await loadProducts();
      }
    };
    
    initializeData();
  }, [loadCompanyData, activeTab]);

  // Loading state component
  const LoadingSpinner = () => (
    <div className="min-h-screen bg-white flex items-center justify-center p-4">
      <div className="text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
        <p className="text-gray-600 text-sm">
          Loading your dashboard...
        </p>
      </div>
    </div>
  );

  // Share link component
  const ShareLinkSection = () => (
    <div className="bg-white  w-[100%] border-b border-blue-100 p-4">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-shrink-0">
          <span className="text-gray-700 text-sm font-medium whitespace-nowrap flex items-center">
            <i className="bi bi-share mr-2"></i>
            Store Link:
          </span>
        </div>

        <div className="flex items-center text-blue-600 flex-1 min-w-0 bg-blue-20 rounded-lg border border-blue-200 overflow-hidden">
          <input
            type="text"
            value={shareLink}
            readOnly
            disabled
            className="flex-1 px-3 py-2 text-sm bg-transparent border-none outline-none truncate min-w-0"
          />

          <button
            onClick={copyToClipboard}
            disabled={isCopying}
            className={`
              px-4 py-2 border-l border-blue-200 text-sm font-medium transition-all duration-200 flex items-center
              ${
                isCopying
                  ? 'text-green-600 bg-green-50'
                  : 'text-blue-600 hover:text-blue-700 hover:bg-blue-50'
              }
            `}
          >
            {isCopying ? (
              <>
                <i className="bi bi-check2 mr-1"></i>
                Copied!
              </>
            ) : (
              <>
                <i className="bi bi-copy mr-1"></i>
                Copy
              </>
            )}
          </button>
        </div>
      </div>

      {/* See Store Button */}
      <div className="mt-3 flex justify-start">
        <button
          onClick={() => window.open(shareLink, "_blank")}
          className="flex items-center text-sm text-blue-600 hover:text-blue-700 font-medium transition-all duration-200"
        >
          <i className="bi bi-eye mr-1"></i>
          See Store
        </button>
      </div>
    </div>
  );

  // Tab content component
  const renderTabContent = () => {
    switch (activeTab) {
      case TAB_CONFIG.PRODUCTS:
        return (
          <>
            <ShareLinkSection />
            <div className="w-full">
              <ProductsList
                products={products}
                onProductUpdate={() => loadProducts(1, currentLimit, false)}
                setCurrentLimit={handleLimitChange}
                setCurrentPage={handlePageChange}
                pagination={pagination}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
              />
            </div>
          </>
        );
      
      case TAB_CONFIG.CREATE_PRODUCT:
        return (
          <div className="lg:p-3">
            <CreateProduct onProductCreated={productSubmitted} />
          </div>
        );
      
      case TAB_CONFIG.CUSTOMERS:
        return (
          <div className="lg:p-3">
            <CustomersList onTabChange={handleTabChange} />
          </div>
        );
      
      case TAB_CONFIG.CREATE_CUSTOMER:
        return (
          <div className="lg:p-3">
            <CreateCustomer />
          </div>
        );
      
      case TAB_CONFIG.MeoAI:
        return (
          <>
            <div className="border-b border-gray-200">
              <h1 className="text-lg font-semibold p-4 ">
                Meo AI
              </h1>
            </div>
            <div className="p-8">
              <ComingSoon />
            </div>
          </>
        );
      
      case TAB_CONFIG.CATEGORY:
        return (
          <div className="lg:p-3">
            <CreateCategory />
          </div>
        );
      
      case TAB_CONFIG.ORDERS:
        return (
          <div className="lg:p-3">
            <OrdersPage />
          </div>
        );
      
      default:
        return (
          <>
            <ShareLinkSection />
            <div className="w-full">
              <ProductsList 
                products={products} 
                onProductUpdate={() => loadProducts(1, currentLimit, false)}
                setCurrentLimit={handleLimitChange}
                setCurrentPage={handlePageChange}
                pagination={pagination}
                isLoading={isLoading}
                onLoadMore={handleLoadMore}
              />
            </div>
          </>
        );
    }
  };

  // Tab configuration
  const tabs = [
    { 
      id: TAB_CONFIG.PRODUCTS, 
      label: 'Products', 
      icon: <i className="bi bi-box mr-2"></i>
    },
    { 
      id: TAB_CONFIG.CREATE_PRODUCT, 
      label: 'Add Item', 
      icon: <i className="bi bi-plus-square mr-2"></i>
    },
    { 
      id: TAB_CONFIG.CUSTOMERS, 
      label: 'Customers', 
      icon: <i className="bi bi-people mr-2"></i>
    },
    { 
      id: TAB_CONFIG.CREATE_CUSTOMER, 
      label: 'Add Customer', 
      icon: <i className="bi bi-person-plus mr-2"></i>
    },
    { 
      id: TAB_CONFIG.MeoAI, 
      label: 'Meo AI', 
      icon: <i className="bi bi-soundwave mr-2"></i>
    },
    { 
      id: TAB_CONFIG.CATEGORY, 
      label: 'Category', 
      icon: <i className="bi bi-folder-plus mr-2"></i>
    },
    {
      id: TAB_CONFIG.ORDERS,
      label: 'Orders',
      icon: <i className="bi bi-view-list mr-2"></i>
    }
  ];

  if (isLoading && activeTab === TAB_CONFIG.PRODUCTS) {
    return <LoadingSpinner />;
  }

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
          <div className={`h-full ${theme === 'dark' ? 'dark' : ''}`}>
    <div className="h-full w-full max-w-[100vw] mx-auto md:max-w-[1000px] lg:max-w-[75vw] overflow-y-auto md:pt-2 pb-12">
      <div className="w-[100%] max-w-[100%] mx-auto">
        {/* Products Container */}
        <div className="bg-white rounded-xl w-[100%] max-w-[100%] mx-auto overflow-x-hidden">
          {/* Tab Navigation */}
          <div className="border-b border-gray-200">
            {/* NAV (sticky header) */}
            <nav
              className="bg-white sticky top-0 z-10"
              aria-label="Product management tabs"
            >
              {/* Outer wrapper: keeps nav width constrained to viewport and prevents page expansion */}
              <div className="w-full overflow-x-hidden">
                {/* Scrollable area: this is the element that actually scrolls horizontally */}
                <motion.div
                  initial={{ x: -60, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                  className="w-[100%] sm:w-full md:w-full xl:w-full overflow-x-auto scrollbar-hide m-auto"
                >
                  <div ref={tabsRef} className="inline-flex flex-nowrap w-max items-center px-1 sm:px-2 py-1">
                    {tabs.map((tab) => (
                      <button
                        key={tab.id}
                        data-tab-id={tab.id}
                        onClick={() => handleTabChange(tab.id)}
                        className={`flex-shrink-0 flex items-center px-4 py-3 text-sm font-medium transition-colors whitespace-nowrap ${
                          activeTab === tab.id
                            ? "text-blue-600 border-b-2 border-blue-600"
                            : "text-gray-500 hover:text-gray-700 border-b-2 border-transparent"
                        }`}
                      >
                        {tab.icon}
                        <span className="truncate">{tab.label}</span>
                      </button>
                    ))}
                  </div>
                </motion.div>
              </div>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="w-full flex flex-col pt-3 ">
             {!currentAccountDetails?.account_number ? (
          currentUser?.name_change_status === "under-review" ? (
            <div className="mb-6">
              <AccountUnderReview user={currentUser} />
            </div>
          ) : (
            <GenerateAccountCard 
              onGenerateAccount={handleGenerateAccount} 
              user={currentUser} 
            />
          )
        ) : currentAccountDetails?.is_pre_paymeo > 0 ? (
          <GetNewAccountCard 
            OnCreateNewAccount={handleNewWalletAccount} 
            user={currentUser} 
          />
        ) : (
            renderTabContent()
                    )}
          </div>
        </div>
      </div>
    </div>
    </div>
    </ThemeContext.Provider>  
  );
};

export default ProductsPage;